import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useToast } from '../contexts/ToastContext';
import { apiRequest } from '../services/api';
import TopPromoBar from '../components/TopPromoBar';
import Header from '../components/Header';
import logo from '../logo.png';
import config from '../config';

function PaymentCallback({ cart, onCartUpdate }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { showSuccess, showError } = useToast();
  const { app } = config;
  const APP_NAME = app.name;
  
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null); // 'success', 'failed', 'pending'
  const [orderNumber, setOrderNumber] = useState(null);
  const [user, setUser] = useState(null);
  
  // Function to clear the shopping cart
  const clearCart = useCallback(() => {
    // Clear cart from localStorage
    localStorage.setItem('cart', JSON.stringify([]));
    // Update cart state if onCartUpdate is provided
    if (onCartUpdate) {
      onCartUpdate([]);
    }
    // Clear pending order items from sessionStorage
    sessionStorage.removeItem('pending_order_items');
    sessionStorage.removeItem('pending_payment_reference');
  }, [onCartUpdate]);

  useEffect(() => {
    // Get user from localStorage
    try {
      const savedUser = localStorage.getItem('user');
      if (savedUser) {
        setUser(JSON.parse(savedUser));
      }
    } catch (error) {
      console.error('Error loading user:', error);
    }

    // Get all Hubtel parameters (they may send different parameter names)
    const reference = searchParams.get('reference') || searchParams.get('clientReference') || searchParams.get('client_reference');
    const checkoutId = searchParams.get('checkoutId') || searchParams.get('checkout_id');
    const hubtelStatus = searchParams.get('status');
    const responseCode = searchParams.get('responseCode') || searchParams.get('response_code');
    const orderNumberFromUrl = searchParams.get('order_number');
    
    // Log all parameters for debugging
    console.log('Payment callback parameters:', {
      reference,
      checkoutId,
      status: hubtelStatus,
      responseCode,
      orderNumber: orderNumberFromUrl,
      allParams: Object.fromEntries(searchParams.entries()),
    });
    
    // If we have status and order_number from backend redirect, use them directly
    if (hubtelStatus === 'success' && orderNumberFromUrl) {
      setStatus('success');
      setOrderNumber(orderNumberFromUrl);
      setLoading(false);
      
      // Clear the entire shopping cart after successful order
      clearCart();
      
      showSuccess(`Payment successful! Order ${orderNumberFromUrl} has been confirmed.`);
      
      // Redirect to orders page after 3 seconds
      setTimeout(() => {
        navigate('/orders', {
          state: {
            orderSuccess: true,
            orderNumber: orderNumberFromUrl,
          },
        });
      }, 3000);
      return;
    }
    
    if (!reference && !checkoutId) {
      showError('Payment reference not found');
      setStatus('failed');
      setLoading(false);
      return;
    }

    const paymentReference = reference;
    
    // Verify payment and get order details
    verifyPayment(paymentReference, checkoutId, hubtelStatus, responseCode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const verifyPayment = async (reference, checkoutId, hubtelStatus, responseCode) => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        showError('Please log in to view payment status');
        navigate('/');
        return;
      }

      // Build callback URL with all Hubtel parameters - use API endpoint
      const params = new URLSearchParams();
      if (reference) params.append('reference', reference);
      if (checkoutId) params.append('checkoutId', checkoutId);
      if (hubtelStatus) params.append('status', hubtelStatus);
      if (responseCode) params.append('responseCode', responseCode);
      
      const callbackUrl = `/api/payment/callback?${params.toString()}`;
      
      const response = await apiRequest(callbackUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.status === 'success' && response.order_number) {
        // Payment successful - order was created
        setStatus('success');
        setOrderNumber(response.order_number);
        
        // Clear the entire shopping cart after successful order
        clearCart();
        
        showSuccess(`Payment successful! Order ${response.order_number} has been confirmed.`);
        
        // Redirect to orders page after 3 seconds
        setTimeout(() => {
          navigate('/orders', {
            state: {
              orderSuccess: true,
              orderNumber: response.order_number,
            },
          });
        }, 3000);
      } else if (response.status === 'cancelled' || response.status === 'failed') {
        // Payment failed or cancelled - redirect back to payment page for retry
        setStatus('failed');
        showError('Payment was cancelled or failed. You can try again.');
        
        // Redirect back to payment page to retry
        setTimeout(() => {
          navigate('/payment', {
            state: {
              error: response.status === 'cancelled' ? 'Payment was cancelled' : 'Payment failed',
              retry: true,
            },
          });
        }, 2000);
      } else if (response.status === 'expired') {
        // Payment session expired
        setStatus('failed');
        showError('Payment session expired. Please try again.');
        
        setTimeout(() => {
          navigate('/checkout');
        }, 2000);
      } else {
        // Payment still pending or unknown status
        setStatus('pending');
        showError('Payment status is being verified. Please check your orders page.');
        
        setTimeout(() => {
          navigate('/orders');
        }, 3000);
      }
    } catch (error) {
      console.error('Error verifying payment:', error);
      
      // If error is 404 (order not created), redirect back to payment page
      if (error.message && error.message.includes('404')) {
        setStatus('failed');
        showError('Payment failed. Please try again.');
        
        setTimeout(() => {
          navigate('/payment', {
            state: {
              error: 'Payment failed',
              retry: true,
            },
          });
        }, 2000);
      } else {
        setStatus('pending');
        showError('Payment status is being verified. Please check your orders page.');
        
        setTimeout(() => {
          navigate('/orders');
        }, 3000);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="App">
      <TopPromoBar appName={APP_NAME} />
      <Header 
        logo={logo} 
        appName={APP_NAME}
        cartCount={0}
        onCartClick={() => navigate('/')}
        cartButtonRef={React.createRef()}
        searchQuery=""
        onSearchChange={() => {}}
        activeFilter={null}
        onFilterChange={() => {}}
        onCategoryFilterChange={() => {}}
        user={user}
        onLoginSuccess={() => {}}
        onLogout={() => {}}
      />
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 pt-[100px] sm:pt-[120px] pb-12">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-xl sm:rounded-2xl border-2 border-gray-200/50 p-6 sm:p-8 shadow-lg">
            {loading ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent mb-4"></div>
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">Verifying Payment...</h2>
                <p className="text-sm sm:text-base text-gray-600">Please wait while we confirm your payment</p>
              </div>
            ) : status === 'success' ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Payment Successful!</h2>
                {orderNumber && (
                  <p className="text-lg text-gray-700 mb-4">Order {orderNumber} has been confirmed</p>
                )}
                <p className="text-sm text-gray-600 mb-6">Redirecting to your orders...</p>
                <button
                  onClick={() => navigate('/orders')}
                  className="px-6 py-3 bg-primary text-white rounded-lg font-semibold hover:bg-primary-dark transition-colors"
                >
                  View Orders
                </button>
              </div>
            ) : status === 'failed' ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Payment Failed</h2>
                <p className="text-sm text-gray-600 mb-6">Please try again or contact support</p>
                <button
                  onClick={() => navigate('/orders')}
                  className="px-6 py-3 bg-primary text-white rounded-lg font-semibold hover:bg-primary-dark transition-colors"
                >
                  View Orders
                </button>
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Verifying Payment</h2>
                <p className="text-sm text-gray-600 mb-6">Please check your orders page for the latest status</p>
                <button
                  onClick={() => navigate('/orders')}
                  className="px-6 py-3 bg-primary text-white rounded-lg font-semibold hover:bg-primary-dark transition-colors"
                >
                  View Orders
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default PaymentCallback;

